import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Terminal, Globe, Users, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Command {
  id: string;
  command: string;
  description: string;
  response_template: string;
  handler_type: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface CommandGroupLink {
  command_id: string;
  group_id: string;
}

const emptyForm = { command: "/", description: "", response_template: "", handler_type: "static", active: true, prompt_complement: "" };

function useOrgId() {
  return useQuery({
    queryKey: ["my-org-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase.from("org_members").select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
      return data?.org_id ?? null;
    },
    staleTime: Infinity,
  });
}

export default function Commands() {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [triggerCommand, setTriggerCommand] = useState<Command | null>(null);
  const [triggerGroupIds, setTriggerGroupIds] = useState<string[]>([]);
  const [triggering, setTriggering] = useState(false);

  const { data: commands = [], isLoading } = useQuery({
    queryKey: ["syra_commands"],
    queryFn: async () => {
      const { data, error } = await supabase.from("syra_commands").select("*").order("command");
      if (error) throw error;
      return data as Command[];
    },
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["syra_groups_list"],
    queryFn: async () => {
      const { data, error } = await supabase.from("syra_groups").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: commandGroups = [] } = useQuery({
    queryKey: ["syra_command_groups"],
    queryFn: async () => {
      const { data, error } = await supabase.from("syra_command_groups").select("command_id, group_id");
      if (error) throw error;
      return data as CommandGroupLink[];
    },
  });

  // Map command_id -> group_ids
  const commandGroupMap = new Map<string, string[]>();
  commandGroups.forEach((cg) => {
    const arr = commandGroupMap.get(cg.command_id) || [];
    arr.push(cg.group_id);
    commandGroupMap.set(cg.command_id, arr);
  });

  const groupNameMap = new Map(groups.map((g) => [g.id, g.name]));

  const upsert = useMutation({
    mutationFn: async () => {
      let commandId = editingId;
      if (editingId) {
        const { error } = await supabase.from("syra_commands").update(form).eq("id", editingId);
        if (error) throw error;
      } else {
        if (!orgId) throw new Error("Organização não encontrada");
        const { data, error } = await supabase.from("syra_commands").insert({ ...form, org_id: orgId }).select("id").single();
        if (error) throw error;
        commandId = data.id;
      }

      // Sync command_groups: delete all then insert selected
      if (commandId) {
        await supabase.from("syra_command_groups").delete().eq("command_id", commandId);
        if (selectedGroupIds.length > 0) {
          const rows = selectedGroupIds.map((gid) => ({ command_id: commandId!, group_id: gid }));
          const { error: linkErr } = await supabase.from("syra_command_groups").insert(rows);
          if (linkErr) throw linkErr;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syra_commands"] });
      queryClient.invalidateQueries({ queryKey: ["syra_command_groups"] });
      toast.success(editingId ? "Comando atualizado" : "Comando criado");
      closeDialog();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("syra_commands").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["syra_commands"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("syra_commands").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["syra_commands"] });
      queryClient.invalidateQueries({ queryKey: ["syra_command_groups"] });
      toast.success("Comando removido");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function openNew() {
    setTriggerDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setSelectedGroupIds([]);
    setDialogOpen(true);
  }

  function openEdit(cmd: Command) {
    setTriggerDialogOpen(false);
    setEditingId(cmd.id);
    setForm({
      command: cmd.command,
      description: cmd.description,
      response_template: cmd.response_template,
      handler_type: cmd.handler_type,
      active: cmd.active,
      prompt_complement: (cmd as any).prompt_complement || "",
    });
    setSelectedGroupIds(commandGroupMap.get(cmd.id) || []);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setSelectedGroupIds([]);
  }

  function toggleGroup(groupId: string) {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  }

  function openTrigger(cmd: Command) {
    closeDialog();
    const linked = commandGroupMap.get(cmd.id) || [];
    setTriggerCommand(cmd);
    setTriggerGroupIds(linked.length > 0 ? [...linked] : []);
    setTriggerDialogOpen(true);
  }

  function toggleTriggerGroup(groupId: string) {
    setTriggerGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  }

  async function executeTrigger() {
    if (!triggerCommand || triggerGroupIds.length === 0) return;
    setTriggering(true);
    try {
      const { data, error } = await supabase.functions.invoke("execute-command", {
        body: { command_id: triggerCommand.id, group_ids: triggerGroupIds },
      });
      if (error) throw new Error(error.message);
      const results = data?.results || [];
      const ok = results.filter((r: any) => r.ok).length;
      const fail = results.filter((r: any) => !r.ok);
      if (fail.length === 0) {
        toast.success(`${triggerCommand.command} disparado em ${ok} grupo(s)`);
      } else {
        toast.warning(`${ok} sucesso, ${fail.length} falha(s)`, {
          description: fail.map((f: any) => `${f.group}: ${f.error}`).join("; "),
        });
      }
      setTriggerDialogOpen(false);
    } catch (e: any) {
      toast.error(e.message || "Erro ao disparar comando");
    } finally {
      setTriggering(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Terminal className="h-6 w-6 text-secondary" /> Comandos do Bot
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie os comandos disponíveis nos grupos de WhatsApp
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" /> Novo Comando
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Comandos Cadastrados</CardTitle>
          <CardDescription>
            Comandos <Badge variant="outline" className="ml-1">builtin</Badge> possuem lógica especial.
            Comandos <Badge variant="secondary" className="ml-1">static</Badge> respondem com o template configurado.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-center py-8">Carregando...</p>
          ) : commands.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum comando cadastrado.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Comando</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Escopo</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commands.map((cmd) => {
                  const linkedGroups = commandGroupMap.get(cmd.id) || [];
                  const isGlobal = linkedGroups.length === 0;
                  return (
                    <TableRow key={cmd.id}>
                      <TableCell className="font-mono font-semibold">{cmd.command}</TableCell>
                      <TableCell className="max-w-xs truncate">{cmd.description}</TableCell>
                      <TableCell>
                        <Badge variant={cmd.handler_type === "builtin" ? "outline" : "secondary"}>
                          {cmd.handler_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {isGlobal ? (
                          <Badge variant="outline" className="gap-1">
                            <Globe className="h-3 w-3" /> Todos
                          </Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {linkedGroups.map((gid) => (
                              <Badge key={gid} variant="secondary" className="text-xs">
                                {groupNameMap.get(gid) || gid.slice(0, 8)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={cmd.active}
                          onCheckedChange={(v) => toggleActive.mutate({ id: cmd.id, active: v })}
                        />
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="icon" title="Disparar comando" onClick={() => openTrigger(cmd)}>
                          <Play className="h-4 w-4 text-primary" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(cmd)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm("Remover este comando?")) remove.mutate(cmd.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Comando" : "Novo Comando"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comando</Label>
              <Input
                value={form.command}
                onChange={(e) => setForm({ ...form, command: e.target.value })}
                placeholder="/meucomando"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="O que este comando faz"
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.handler_type} onValueChange={(v) => setForm({ ...form, handler_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Static (resposta fixa)</SelectItem>
                  <SelectItem value="builtin">Builtin (lógica especial)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.handler_type === "static" && (
              <div>
                <Label>Template de Resposta</Label>
                <Textarea
                  value={form.response_template}
                  onChange={(e) => setForm({ ...form, response_template: e.target.value })}
                  placeholder="Texto que o bot enviará ao receber este comando"
                  rows={4}
                />
              </div>
            )}

            <div>
              <Label>Complemento de Prompt (opcional)</Label>
              <Textarea
                value={form.prompt_complement}
                onChange={(e) => setForm({ ...form, prompt_complement: e.target.value })}
                placeholder="Instrução adicional enviada ao agente de IA quando este comando é invocado. Deixe vazio se não for necessário."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Quando preenchido, este texto é adicionado ao prompt do agente de IA ao processar o comando.
              </p>
            </div>

            {/* Group scope selector */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" /> Grupos permitidos
              </Label>
              <p className="text-xs text-muted-foreground">
                Se nenhum grupo for selecionado, o comando funciona em todos os grupos.
              </p>
              <div className="max-h-40 overflow-y-auto border rounded-md divide-y divide-border">
                {groups.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">Nenhum grupo cadastrado</p>
                ) : (
                  groups.map((g) => (
                    <label
                      key={g.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedGroupIds.includes(g.id)}
                        onCheckedChange={() => toggleGroup(g.id)}
                      />
                      <span className="text-sm text-foreground">{g.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedGroupIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedGroupIds.length} grupo(s) selecionado(s)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancelar</Button>
            <Button onClick={() => upsert.mutate()} disabled={!form.command || form.command === "/"}>
              {editingId ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trigger Command Dialog */}
      <Dialog open={triggerDialogOpen} onOpenChange={setTriggerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              Disparar: <span className="font-mono">{triggerCommand?.command}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Selecione os grupos para os quais deseja disparar este comando agora.
            </p>
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y divide-border">
              {groups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3">Nenhum grupo cadastrado</p>
              ) : (
                groups.map((g) => (
                  <label
                    key={g.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors cursor-pointer"
                  >
                    <Checkbox
                      checked={triggerGroupIds.includes(g.id)}
                      onCheckedChange={() => toggleTriggerGroup(g.id)}
                    />
                    <span className="text-sm text-foreground">{g.name}</span>
                  </label>
                ))
              )}
            </div>
            {triggerGroupIds.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {triggerGroupIds.length} grupo(s) selecionado(s)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTriggerDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={executeTrigger}
              disabled={triggerGroupIds.length === 0 || triggering}
            >
              {triggering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              {triggering ? "Disparando..." : "Disparar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
